import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package } from "lucide-react";

// Map an order's payment lifecycle to a human-friendly line + a pill colour.
// Only `paid` shows the "Paid via …" wording. Every other state is clearly
// distinguished so buyers never mistake a pending / failed / cancelled /
// expired attempt for a completed purchase.
function paymentLine(o) {
  const provider = (o.payment.provider || "").replace("_", " ").toUpperCase();
  const phone = o.payment.phone ? ` · ${o.payment.phone}` : "";
  const st = o.payment.status;
  if (st === "paid") return { text: `Paid via ${provider}${phone}`, cls: "text-[#1B804E]" };
  if (st === "pending") return { text: `Payment pending${phone ? ` · ${o.payment.phone}` : ""}`, cls: "text-[#8C6D53]" };
  if (st === "failed") return { text: "Payment failed — please try again", cls: "text-[#D93838]" };
  if (st === "abandoned" || o.status === "cancelled") return { text: "Payment cancelled", cls: "text-[#D93838]" };
  if (st === "expired" || o.status === "expired") return { text: "Payment expired — please try again", cls: "text-[#8C6D53]" };
  if (st === "reversed") return { text: "Payment reversed", cls: "text-[#D93838]" };
  return { text: `Payment ${st || "unknown"}`, cls: "text-[#5C6660]" };
}

function statusPill(order) {
  const s = order.status;
  if (s === "processing" || s === "shipped" || s === "delivered") return "bg-[#1B804E] text-white";
  if (s === "pending_payment") return "bg-[#8C6D53] text-white";
  if (s === "failed") return "bg-[#D93838] text-white";
  if (s === "cancelled") return "bg-[#5C6660] text-white";
  if (s === "expired") return "bg-[#A8926F] text-white";
  return "bg-[#5C6660] text-white";
}

export default function Orders() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user, loading]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-extrabold text-[#1E4D3B]">My orders</h1>

      {orders.length === 0 ? (
        <div className="card-farm p-10 text-center mt-6">
          <Package className="w-12 h-12 mx-auto text-[#8C6D53]" />
          <div className="font-bold mt-3">No orders yet</div>
          <div className="text-sm text-[#5C6660]">Once you complete a checkout it will appear here.</div>
        </div>
      ) : (
        <div className="space-y-4 mt-6">
          {orders.map((o) => {
            const pay = paymentLine(o);
            return (
            <div key={o.id} className="card-farm p-5" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-mono text-xs text-[#8C6D53]">Ref {o.payment.reference}</div>
                  <div className="font-bold text-[#1E4D3B]">{new Date(o.created_at).toLocaleString()}</div>
                  <div className={`text-xs mt-1 font-semibold ${pay.cls}`} data-testid={`order-${o.id}-payment-line`}>{pay.text}</div>
                </div>
                <div className="text-right">
                  <div className="text-[#C85A32] font-black text-2xl">GH₵ {o.total.toFixed(2)}</div>
                  <span
                    className={`text-[10px] px-2 py-1 rounded-full uppercase font-bold tracking-widest ${statusPill(o)}`}
                    data-testid={`order-${o.id}-status-pill`}
                  >{o.status.replace("_", " ")}</span>
                </div>
              </div>
              <div className="h-px bg-[#E5D9C8] my-4" />
              <div className="space-y-2">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <div>
                      <div className="font-semibold">{it.product_name} × {it.quantity}</div>
                      <div className="text-xs text-[#5C6660]">by {it.seller_name}</div>
                    </div>
                    <div className="font-semibold">GH₵ {(it.price * it.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-[#5C6660]">Delivery: {o.delivery_address}, {o.delivery_region}</div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
